import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";
import { toast } from "react-toastify";
import { workflowApi } from "../../Apis/Api"; // Adjust path as needed
import useDebounce from "../../hook/useDebounce";
import TableDataNoAvailable from "../ui/TableDataNoAvailable";
import { Button } from "react-bootstrap"; // Added for Button component
import { Clock, Loader2, Pin, PlayCircle, Printer, Trash2 } from "lucide-react";
import { CHECK_IN_PHOTO } from "../../utils/Contants";
import { apiCall } from "../../Apis/apiCall";
import { usePermissions } from "../../context/PermissionContext";
import AttendanceOverlay from "./AttendanceOverlay";
import { handleApiResponse } from "../../utils/response";
import ErrorAlert from "../ui/ErrorAlert";
import ReceiptComponent from "../workflow/ReceiptComponent";
import TicketComponent from "../workflow/TicketComponent";
import StepHistoryModal from "../workflow/StepHistoryModal";

const Instance = () => {
  const [instances, setInstances] = useState([]);
  const [isContinuing, setIsContinuing] = useState({});
  const [continuing, setContinuing] = useState(false);
  const [isCanceling, setIsCanceling] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedInstance, setSelectedInstance] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showCancelConfirmModal, setShowCancelConfirmModal] = useState(false);
  const [instanceToCancel, setInstanceToCancel] = useState(null);
  const [showAttendanceOverlay, setShowAttendanceOverlay] = useState(false);
  const [attendanceCheck, setAttendanceCheck] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    perPage: 10,
    total: 0,
  });
  const [filters, setFilters] = useState({
    search: "",
    status: "",
  });
  const [sortConfig, setSortConfig] = useState({
    field: null,
    direction: "asc",
  });

  const navigate = useNavigate();
  const debouncedSearch = useDebounce(filters.search, 500);
  const { isAttendanceMarked, refreshAuthStatus } = usePermissions();

  const handleLogout = async () => {
    localStorage.clear();
    await refreshAuthStatus();
    toast.success("Logout Successful");
    navigate("/sign-in", { replace: true });
  };

  useEffect(() => {
    if (attendanceCheck && isAttendanceMarked) {
      setShowAttendanceOverlay(false);
      setAttendanceCheck(false);
      if (selectedInstance) {
        handleContinue(selectedInstance);
      } else if (instanceToCancel) {
        setShowCancelConfirmModal(true);
      }
    }
  }, [isAttendanceMarked, attendanceCheck, selectedInstance, instanceToCancel]);

  const handleAttendanceSuccess = () => {
    setShowAttendanceOverlay(false);
    setAttendanceCheck(false);
    refreshAuthStatus();
  };

  const handleCloseOverlay = () => {
    setShowAttendanceOverlay(false);
    setAttendanceCheck(false);
  };

  useEffect(() => {
    fetchInstances();
  }, [
    pagination.currentPage,
    pagination.perPage,
    debouncedSearch,
    filters.status,
    sortConfig,
  ]);

  const fetchInstances = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page: pagination.currentPage,
        per_page: pagination.perPage,
        search: debouncedSearch,
        status: filters.status,
        ...(sortConfig.field && {
          sort_by: sortConfig.field,
          sort_order: sortConfig.direction,
        }),
      };

      const response = await workflowApi.getMyInstances(params);

      if (!response.success) {
        throw new Error(
          response.message ||
            response.error ||
            "Failed to fetch workflow instances"
        );
      }

      if (!Array.isArray(response.data)) {
        throw new Error(
          response.message || "Invalid API response: instances data missing"
        );
      }

      const data = response.data;

      const formattedData = data.map((instance, index) => {
        let stepCount = instance.step_history?.length || 0;
        if (instance.current_step) {
          const currentStepIndex = instance.step_history?.findIndex(
            (step) => step.step_label === instance.current_step
          );
          stepCount =
            currentStepIndex >= 0
              ? currentStepIndex + 1
              : (instance.step_history?.length || 0) + 1;
        }

        const isCompleted =
          instance.steps_remaining === 0 && instance.steps_filled > 0;
        const actualStatus = isCompleted ? "completed" : instance.status;

        return {
          id: instance.instance_id || index + 1,
          workflow_name: instance.workflow_name || "Unknown Workflow",
          workflow_id: instance.workflow_id || "-",
          current_step_label: isCompleted
            ? "Completed"
            : instance.current_step || "-",
          current_step_code: isCompleted
            ? "COMPLETED"
            : instance.current_step || "-",
          step_count: stepCount,
          status: actualStatus.charAt(0).toUpperCase() + actualStatus.slice(1),
          status_label: actualStatus || "-",
          assigned_to: instance.assigned_to_staff || "Unassigned",
          assigned_by: instance.assigned_by || "-",
          created_at: instance.created_at
            ? new Date(instance.created_at).toLocaleDateString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })
            : "-",
          raw_created_at: instance.created_at || "-",
          step_history: instance.step_history || [],
          location_id: instance.location_id || null,
          steps_filled: instance.steps_filled || 0,
          steps_remaining: instance.steps_remaining || 0,
          total_steps: instance.total_steps || 0,
          step_picked: instance.step_picked || false,
        };
      });

      setInstances(formattedData);
      setPagination((prev) => ({
        ...prev,
        currentPage: response.pagination?.current_page || 1,
        perPage: response.pagination?.per_page || 10,
        total: response.pagination?.total || formattedData.length,
      }));
    } catch (err) {
      setError(err.message || "Failed to fetch workflow instances");
      setInstances([]);
      handleApiResponse(err, "Failed to fetch workflow instances", "get");
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (instance) => {
    setSelectedInstance(instance);
    setShowModal(true);
  };

  const handleContinue = async (instance) => {
    if (isContinuing[instance.id]) return;

    if (!isAttendanceMarked) {
      setAttendanceCheck(true);
      setShowAttendanceOverlay(true);
      return;
    }

    setIsContinuing((prev) => ({ ...prev, [instance.id]: true }));
    setContinuing(true);
    try {
      const stepResponse = await workflowApi.getWorkflowStep(instance.id);
      const responseData = stepResponse?.data || stepResponse;

      const stepId =
        responseData?.new_step_id ||
        responseData?.step_id ||
        responseData?.id ||
        responseData?.step_code;

      if (!stepId) {
        throw new Error(
          "Invalid step response: Missing step_id or new_step_id."
        );
      }

      if (responseData?.isAssigned === "N") {
        toast.info("This workflow step is assigned to another user.");
        setIsContinuing((prev) => ({ ...prev, [instance.id]: false }));
        setContinuing(false);
        return;
      }

      const lockStepPayload = {
        workflow_instance_id: instance.id,
        workflow_step_id: stepId,
      };

      const lockStepResponse = await apiCall(
        "POST",
        "/workflow-instance/lock-step",
        lockStepPayload,
        navigate
      );

      if (!lockStepResponse.data.success) {
        throw new Error(
          lockStepResponse.data.message || "Failed to lock workflow step."
        );
      }

      navigate(`/workflows-running`, {
        state: {
          instanceId: instance.id,
          workflowId: instance.workflow_id,
          workflowName: instance.workflow_name,
          stepCount: instance.step_count,
          locationId: instance.location_id || null,
        },
      });
    } catch (error) {
      toast.error(error.message || "Failed to continue workflow.");
    } finally {
      setIsContinuing((prev) => ({ ...prev, [instance.id]: false }));
      setContinuing(false);
    }
  };

  const handleCancel = (instance) => {
    if (!isAttendanceMarked) {
      setAttendanceCheck(true);
      setShowAttendanceOverlay(true);
      setInstanceToCancel(instance);
      return;
    }
    setInstanceToCancel(instance);
    setShowCancelConfirmModal(true);
  };

  const confirmCancel = async () => {
    if (!instanceToCancel?.id) {
      toast.error("Cannot cancel workflow. Invalid instance data.");
      setShowCancelConfirmModal(false);
      return;
    }

    setIsCanceling((prev) => ({ ...prev, [instanceToCancel.id]: true }));

    try {
      const response = await apiCall(
        "POST",
        `/cancel/${instanceToCancel.id}/instance`,
        {},
        navigate
      );

      if (response.data.success) {
        toast.success("Workflow instance canceled successfully!");
        fetchInstances();
      } else {
        throw new Error(
          response.data.message || "Failed to cancel workflow instance"
        );
      }
    } catch (err) {
      const errorMessage = err.message || "Failed to cancel workflow instance.";
      toast.error(errorMessage);
    } finally {
      setIsCanceling((prev) => ({ ...prev, [instanceToCancel.id]: false }));
      setShowCancelConfirmModal(false);
      setInstanceToCancel(null);
    }
  };

  const handleCloseCancelModal = () => {
    setShowCancelConfirmModal(false);
    setInstanceToCancel(null);
  };

  // const handleCloseModal = () => {
  //   setShowModal(false);
  //   setSelectedInstance(null);
  // };

  if (loading) {
    return (
      <>
        {Array.from({ length: 5 }).map((_, index) => (
          <ShimmerCard key={index} />
        ))}
      </>
    );
  }

  if (error) {
    return <ErrorAlert error={error} onRetry={fetchInstances} />;
  }

  const totalPages = Math.ceil(pagination.total / pagination.perPage);

  const extractVehicleAndParkingData = (stepHistory) => {
    if (!Array.isArray(stepHistory)) return {};

    const result = {
      vehicle_license_plate: null,
      vehicle_make: null,
      vehicle_model: null,
      vehicle_color: null,
      parking_ticket: null,
      parking_slot_number: null,
      location_name: null,
      parked_time: null,
      front_image: null,
      back_image: null,
      left_image: null,
      right_image: null,
      _image_base_url: CHECK_IN_PHOTO,
    };

    stepHistory.forEach((step) => {
      const data = step?.submitted_data;
      if (!data || typeof data !== "object") return;

      const getFirstImage = (img) => {
        if (!img) return null;
        return Array.isArray(img) ? img[0] : img;
      };

      if (data.parked_time && !result.parked_time) {
        result.parked_time = data.parked_time;
      }
      if (data.location_name && !result.location_name) {
        result.location_name = data.location_name;
      }

      const vehicleFields = [
        "vehicle_license_plate",
        "vehicle_make",
        "vehicle_model",
        "vehicle_color",
      ];

      vehicleFields.forEach((field) => {
        if (data[field] && !result[field]) {
          result[field] = data[field];
        }
      });

      if (data.parking_ticket && !result.parking_ticket) {
        result.parking_ticket = data.parking_ticket;
      } else if (data.ticket_number && !result.parking_ticket) {
        result.parking_ticket = data.ticket_number;
      }

      if (data.parking_slot_number && !result.parking_slot_number) {
        result.parking_slot_number = data.parking_slot_number;
      } else if (data.parking_slot_id && !result.parking_slot_number) {
        result.parking_slot_number = `Slot ${data.parking_slot_id}`;
      }

      const imageFields = [
        "front_image",
        "back_image",
        "left_image",
        "right_image",
      ];

      imageFields.forEach((field) => {
        if (data[field] && !result[field]) {
          result[field] = getFirstImage(data[field]);
        }
      });
    });

    return result;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString("en-US", {
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    });
  };

  const renderSubmittedData = (data, depth = 0, fieldName = "") => {
    if (!data || typeof data !== "object") {
      return <span>{data?.toString() || "-"}</span>;
    }

    // Handle ticket_summary case
    if (fieldName === "ticket_summary") {
      return <TicketComponent data={data} fieldName={fieldName} />;
    }

    // Handle receipt case
    if (fieldName === "reciept_image" || fieldName === "receipt") {
      return <ReceiptComponent data={data} fieldName={fieldName} />;
    }

    if (Array.isArray(data)) {
      return (
        <ul className="list-unstyled ms-3">
          {data.map((item, idx) => (
            <li key={idx}>{renderSubmittedData(item, depth + 1)}</li>
          ))}
        </ul>
      );
    }

    return (
      <ul className={`list-unstyled ${depth > 0 ? "ms-3" : ""}`}>
        {Object.entries(data).map(([key, value]) => {
          const isImageField =
            key.toLowerCase().includes("image") ||
            key.toLowerCase().includes("photo") ||
            key.toLowerCase().includes("receipt_image");

          const displayKey = key
            .replace(/_/g, " ")
            .replace(/\b\w/g, (c) => c.toUpperCase());

          let displayValue;
          if (value === null || value === undefined) {
            displayValue = "-";
          } else if (typeof value === "object") {
            displayValue = renderSubmittedData(value, depth + 1, key);
          } else {
            displayValue = value.toString();
          }

          return (
            <li key={key} className="mb-2 d-flex gap-2 align-items-start">
              <strong className="text-capitalize">{displayKey}:</strong>
              {isImageField && typeof value === "string" && value ? (
                <div className="image-container">
                  <img
                    src={`${CHECK_IN_PHOTO}/${value}`}
                    alt={key}
                    className="img-thumbnail"
                    style={{
                      maxWidth: "150px",
                      maxHeight: "150px",
                    }}
                    onClick={() =>
                      window.open(`${CHECK_IN_PHOTO}/${value}`, "_blank")
                    }
                  />
                </div>
              ) : isImageField && Array.isArray(value) && value.length > 0 ? (
                <div className="d-flex gap-2 flex-wrap">
                  {value.map((img, idx) => (
                    <img
                      key={idx}
                      src={`${CHECK_IN_PHOTO}/${img}`}
                      alt={`${key}-${idx}`}
                      className="img-thumbnail"
                      style={{
                        maxWidth: "150px",
                        maxHeight: "150px",
                      }}
                      onClick={() =>
                        window.open(`${CHECK_IN_PHOTO}/${img}`, "_blank")
                      }
                    />
                  ))}
                </div>
              ) : (
                <div>{displayValue}</div>
              )}
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <>
      <div className="h-100 p-0 radius-12">
        <div className="">
          <div className="table-responsive scroll-sm">
            {instances.length === 0 ? (
              <TableDataNoAvailable message="No workflow instances available." />
            ) : (
              <>
                <div className="">
                  <div className="d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">Recent</h6>
                    {/* {isAttendanceMarked && ( */}
                    <Link
                      to="/workflow-instances"
                      className="btn text-primary-600"
                    >
                      See All
                    </Link>
                    {/* )} */}
                  </div>

                  {instances.map((instance) => {
                    const vehicleData = extractVehicleAndParkingData(
                      instance.step_history
                    );

                    const vehicleImage =
                      vehicleData.front_image ||
                      vehicleData.back_image ||
                      vehicleData.left_image ||
                      vehicleData.right_image;
                    return (
                      <div
                        className="card mt-3 border-0 shadow-sm"
                        key={instance.id}
                      >
                        <div className="card-body rounded-4 p-3 p-md-4">
                          <div className="workflow-item">
                            <div className="d-flex justify-content-between align-items-start">
                              <div
                                className="d-flex align-items-center flex-grow-1 pe-3 pe-md-4 cursor-pointer w-100"
                                onClick={() => handleViewDetails(instance)}
                              >
                                <div className="position-relative me-2 me-md-3">
                                  <img
                                    src={
                                      vehicleImage
                                        ? `${CHECK_IN_PHOTO}/${vehicleImage}`
                                        : "/car-img.jpg"
                                    }
                                    alt="Vehicle"
                                    className="rounded object-cover"
                                    width={80}
                                    height={80}
                                    style={{ objectFit: "cover" }}
                                  />
                                </div>
                                <div className="flex-grow-1">
                                  <p className="mb-1 fw-bold d-flex align-items-center gap-2">
                                    <span className="text-wrap">
                                      {vehicleData?.vehicle_make ||
                                      vehicleData?.vehicle_model
                                        ? `${vehicleData.vehicle_make || ""}${
                                            vehicleData.vehicle_make &&
                                            vehicleData.vehicle_model
                                              ? " - "
                                              : ""
                                          }${vehicleData.vehicle_model || ""}`
                                        : "Resume your instance"} 
                                          
                                    </span>
                                  </p>
                                  <div className="d-flex flex-column small">
                                    <span className="d-flex align-items-center gap-2 mb-1">
                                      <Pin
                                        size={15}
                                        className="flex-shrink-0"
                                      />
                                      <span className="text-wrap">
                                        {[
                                          vehicleData.location_name,
                                          vehicleData.parking_slot_number,
                                          vehicleData.vehicle_color,
                                        ]
                                          .filter(Boolean)
                                          .join(" | ") || "-"}
                                      </span>
                                    </span>
                                    <span className="d-flex align-items-center gap-2">
                                      <Icon
                                        icon="mdi:car-key"
                                        className="icon text-lg"
                                      />
                                      <span className="text-wrap">
                                        {vehicleData.vehicle_license_plate ||
                                          "No plate"}
                                      </span>
                                    </span>
                                    <span className="d-flex align-items-center gap-2">
                                      <Clock
                                        size={15}
                                        className="flex-shrink-0"
                                      />
                                      <span className="text-wrap">
                                        {vehicleData.parked_time
                                          ? formatDate(vehicleData.parked_time)
                                          : "-"}
                                      </span>
                                    </span>
                                    {
                                 instance?.step_picked && <div><span className="badge bg-success text-light border px-3 py-1 rounded-pill fw-medium">
                                    Step picked
                                  </span></div>
                                }
                                  </div>
                                </div>
                              </div>
                              <div className="d-flex flex-column align-items-end h-100 justify-content-between mt-3 mt-md-0 w-100 w-md-auto gap-5">
                                <div className="d-flex gap-2 gap-md-3 align-items-center flex-wrap justify-content-end">
                                  <span className="badge bg-light text-dark border px-3 py-1 rounded-pill fw-medium">
                                    Step {instance.steps_filled}/
                                    {instance.total_steps}
                                  </span>
                                  <div className="d-flex gap-2">
                                    {(instance.status === "In_progress" ||
                                      instance.status === "Completed") && (
                                      <button
                                        className="btn btn-sm btn-link p-0"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setSelectedInstance(instance);
                                          handleContinue(instance);
                                        }}
                                        disabled={
                                          isContinuing[instance.id] ||
                                          instance.status === "Completed" || continuing
                                        }
                                        title={
                                          instance.status === "Completed"
                                            ? "Workflow already completed"
                                            : "Continue workflow"
                                        }
                                      >
                                        {isContinuing[instance.id] &&
                                        instance.status === "In_progress" ? (
                                          <Loader2
                                            size={18}
                                            className="animate-spin"
                                            style={{
                                              animation:
                                                "spin 1s linear infinite",
                                            }}
                                          />
                                        ) : (
                                          <PlayCircle
                                            size={18}
                                            className={
                                              instance.status === "Completed"
                                                ? "text-neutral-900"
                                                : "text-success"
                                            }
                                          />
                                        )}
                                      </button>
                                    )}
                                    {instance.status === "In_progress" && (
                                      <button
                                        className="btn btn-sm btn-link p-0"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleCancel(instance);
                                        }}
                                        disabled={isCanceling[instance.id]}
                                        title="Cancel workflow"
                                      >
                                        {isCanceling[instance.id] ? (
                                          <Loader2
                                            size={18}
                                            className="animate-spin"
                                          />
                                        ) : (
                                          <Trash2
                                            size={18}
                                            className="text-danger-600"
                                          />
                                        )}
                                      </button>
                                    )}
                                  </div>
                                </div>
                                {/* {
                                 instance?.step_picked && <div><span className="badge bg-light text-dark border px-3 py-1 rounded-pill fw-medium">
                                    Step picked
                                  </span></div>
                                } */}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      {/* Step History Modal */}
      <StepHistoryModal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setSelectedInstance(null);
        }}
        instance={selectedInstance}
        title="Instance History"
      />
      {showCancelConfirmModal && instanceToCancel && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          role="dialog"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Cancellation</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseCancelModal}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                <p>
                  Are you sure you want to cancel the workflow instance{" "}
                  <strong>{instanceToCancel.workflow_name}</strong>
                </p>
                <p className="text-neutral-900 small">
                  This action cannot be undone.
                </p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCloseCancelModal}
                  aria-label="Cancel cancellation"
                  disabled={isCanceling[instanceToCancel.id]}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={confirmCancel}
                  aria-label={`Confirm cancel workflow ${instanceToCancel.workflow_name}`}
                  disabled={isCanceling[instanceToCancel.id]}
                >
                  {isCanceling[instanceToCancel.id]
                    ? "Please wait..."
                    : "Delete"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <AttendanceOverlay
        show={showAttendanceOverlay}
        onClose={handleCloseOverlay}
        onLogout={handleLogout}
        onSuccess={handleAttendanceSuccess}
      />
    </>
  );
};

export default Instance;
const ShimmerCard = () => {
  return (
    <div className="card mt-3 border-0 shadow-sm radius-12">
      <div className="card-body rounded-12 p-20">
        <div className="d-flex justify-content-between align-items-start flex-column flex-md-row gap-4">
          {/* Left Side: Image + Vehicle Info */}
          <div
            className="d-flex align-items-start gap-3 flex-grow-1 w-100 cursor-pointer"
            onClick={() => {}} // just to match real behavior
          >
            {/* Vehicle Image */}
            <div className="position-relative me-3">
              <div className="placeholder-glow">
                <div
                  className="placeholder rounded-12"
                  style={{
                    width: "80px",
                    height: "80px",
                    minWidth: "80px",
                    background: "linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%)",
                  }}
                ></div>
              </div>
            </div>

            {/* Text Details */}
            <div className="flex-grow-1">
              {/* Vehicle Make & Model */}
              <div className="placeholder-glow mb-12">
                <span
                  className="placeholder col-9 col-md-6 h-22-px rounded"
                  style={{ height: "22px" }}
                ></span>
              </div>

              {/* Location | Slot | Color */}
              <div className="d-flex align-items-center gap-2 mb-8">
                <div className="placeholder-glow">
                  <Pin size={15} className="text-transparent" />
                </div>
                <div className="placeholder-glow flex-grow-1">
                  <span className="placeholder col-11 col-md-10"></span>
                </div>
              </div>

              {/* License Plate */}
              <div className="d-flex align-items-center gap-2 mb-8">
                <div className="placeholder-glow">
                  <Icon icon="mdi:car-key" className="text-transparent text-lg" />
                </div>
                <div className="placeholder-glow flex-grow-1">
                  <span className="placeholder col-8 col-md-6"></span>
                </div>
              </div>

              {/* Parked Time */}
              <div className="d-flex align-items-center gap-2">
                <div className="placeholder-glow">
                  <Clock size={15} className="text-transparent" />
                </div>
                <div className="placeholder-glow flex-grow-1">
                  <span className="placeholder col-7 col-md-5"></span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side: Step Badge + Action Buttons */}
          <div className="d-flex flex-column align-items-end justify-content-between h-100 mt-3 mt-md-0 w-100 w-md-auto gap-3">
            <div className="d-flex gap-3 align-items-center flex-wrap justify-content-end">
              {/* Step Badge */}
              <div className="placeholder-glow">
                <span
                  className="placeholder rounded-pill"
                  style={{
                    width: "90px",
                    height: "30px",
                    background: "#e0e0e0",
                  }}
                ></span>
              </div>

              {/* Action Buttons */}
              <div className="d-flex gap-3">
                {/* Play Button */}
                <div className="placeholder-glow">
                  <div
                    className="placeholder rounded-circle"
                    style={{
                      width: "40px",
                      height: "40px",
                      background: "#f0f0f0",
                    }}
                  ></div>
                </div>

                {/* Cancel Button */}
                <div className="placeholder-glow">
                  <div
                    className="placeholder rounded-circle"
                    style={{
                      width: "40px",
                      height: "40px",
                      background: "#f0f0f0",
                    }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
